import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useParams, useNavigate, Link } from "react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Store,
  Bell,
  Activity,
  Trash2,
  Pencil,
  TriangleAlert,
  ChevronDown,
  ChevronUp,
  MonitorSmartphone,
  AlertCircle,
  UserPlus,
  Users,
  CalendarCheck,
  ShieldAlert,
  CalendarX,
  Coins,
  UserRoundX,
} from "lucide-react";
import { useAdminAuth } from "@/lib/hooks/useAdminAuth";
import { storeDetailPath, TAB_APPOINTMENTS } from "@/lib/utils/storeTabs";
import {
  getUser,
  getUserStores,
  getUserDeviceTokens,
  getUserActivity,
  getUserGuestlistCheckins,
  updateUser,
  sendUserNotification,
  deleteUser,
  suspendUser,
  reinstateUser,
  flagUserName,
  unflagUserName,
} from "@/lib/api/admin";
import { Spinner, SpinnerCenter } from "@/components/Spinner";
import { TextInput } from "@/components/TextInput";
import { Textarea } from "@/components/Textarea";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { StatusBadge } from "@/components/StatusBadge";
import { Pagination } from "@/components/Pagination";
import { cn } from "@/lib/utils";
import { formatDate, formatDateTime, formatDateTimeWithDay } from "@/lib/utils/date";
import { formatPhone } from "@/lib/utils/phone";
import { formatInt, formatCents } from "@/lib/utils/number";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import type { ActivityLog, MyGuestlistCheckins } from "@/lib/api/types";
import { AdjustPointsBalanceDialog } from "./components/AdjustPointsBalanceDialog";
import { AdjustStampCardDialog } from "./components/AdjustStampCardDialog";

const ACTIVITY_PAGE_SIZE = 20;

const editSchema = z.object({
  firstName: z
    .string()
    .min(1, "First name is required.")
    .max(50, "First name must be 50 characters or less."),
  lastName: z
    .string()
    .min(1, "Last name is required.")
    .max(50, "Last name must be 50 characters or less."),
});

const notificationSchema = z.object({
  title: z.string().min(1, "Title is required.").max(100, "Title must be 100 characters or less."),
  body: z.string().min(1, "Body is required.").max(500, "Body must be 500 characters or less."),
  imageUrl: z.string().optional(),
});

type EditFormValues = z.infer<typeof editSchema>;
type NotificationFormValues = z.infer<typeof notificationSchema>;

// Mirrors ActivityTab.tsx's ACTION_CONFIG labels; unlisted actions fall back to raw text.
const ACTION_LABELS: Record<string, string> = {
  signup_bonus: "Welcome bonus",
  item_claimed: "Item claimed",
  point_reward_redeemed: "Point reward redeemed",
  stamp_earned: "Stamp earned",
  stamp_reward_redeemed: "Stamp reward redeemed",
  points_earned: "Points earned",
  points_spent: "Points spent",
  event_checkin: "Event self check-in",
  event_inviter_rewarded: "Event guest check-in",
  referral_invitee_rewarded: "Referral invitee bonus",
  referral_inviter_rewarded: "Referral inviter bonus",
  store_referral_invitee_rewarded: "Store referral invitee bonus",
  store_referral_inviter_rewarded: "Store referral inviter bonus",
  bundle_items_claimed: "Bundle claimed",
  points_expired: "Points expired",
  stamps_expired: "Stamps expired",
  appointment_checkin: "Appointment check-in",
  appointment_no_show_penalty: "No-show penalty",
  admin_balance_adjusted: "Balance adjusted by support",
};

function formatActionLabel(action: string): string {
  return ACTION_LABELS[action] ?? action.replace(/_/g, " ");
}

function invitedGuestName(a: ActivityLog): string | undefined {
  const name = a.details?.invitee_name;
  return a.action === "event_inviter_rewarded" && typeof name === "string" && name.trim() !== ""
    ? name
    : undefined;
}

function AndroidIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M17.6 9.48l1.84-3.18c.16-.31.04-.69-.26-.85a.637.637 0 0 0-.83.22l-1.88 3.24a11.46 11.46 0 0 0-8.94 0L5.65 5.67a.643.643 0 0 0-.87-.2c-.28.18-.37.54-.22.83L6.4 9.48A10.78 10.78 0 0 0 1 18h22a10.78 10.78 0 0 0-5.4-8.52zM7 15.25a1.25 1.25 0 1 1 0-2.5 1.25 1.25 0 0 1 0 2.5zm10 0a1.25 1.25 0 1 1 0-2.5 1.25 1.25 0 0 1 0 2.5z" />
    </svg>
  );
}

function AppleIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
    </svg>
  );
}

export function UserDetailPage() {
  const { uid } = useParams<{ uid: string }>();
  const user = useAdminAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const [activityOffset, setActivityOffset] = useState(0);
  const [editOpen, setEditOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteConfirmName, setDeleteConfirmName] = useState("");
  const [suspendOpen, setSuspendOpen] = useState(false);
  const [suspendReason, setSuspendReason] = useState("");
  const [reinstateOpen, setReinstateOpen] = useState(false);
  const [flagNameOpen, setFlagNameOpen] = useState(false);
  const [flagNameReason, setFlagNameReason] = useState("");
  const [unflagNameOpen, setUnflagNameOpen] = useState(false);
  const [adjustPointsOpen, setAdjustPointsOpen] = useState(false);
  const [adjustStampsOpen, setAdjustStampsOpen] = useState(false);
  const [dangerExpanded, setDangerExpanded] = useState(false);
  const [selectedCheckinEvent, setSelectedCheckinEvent] = useState<MyGuestlistCheckins | null>(
    null
  );

  const {
    register: registerEdit,
    handleSubmit: handleEditSubmit,
    reset: resetEdit,
    setError: setEditError,
    formState: { errors: editErrors },
  } = useForm<EditFormValues>({
    resolver: zodResolver(editSchema),
    defaultValues: { firstName: "", lastName: "" },
  });

  const {
    register: registerNotif,
    handleSubmit: handleNotifSubmit,
    reset: resetNotif,
    setError: setNotifError,
    formState: { errors: notifErrors },
  } = useForm<NotificationFormValues>({
    resolver: zodResolver(notificationSchema),
    defaultValues: { title: "", body: "", imageUrl: "" },
  });

  const {
    data: profile,
    isLoading,
    isError: profileError,
  } = useQuery({
    queryKey: ["user", uid],
    queryFn: async () => getUser(user!, uid!),
    enabled: !!user && !!uid,
  });

  useEffect(() => {
    if (editOpen && profile) {
      resetEdit({
        firstName: profile.first_name,
        lastName: profile.last_name,
      });
    }
  }, [editOpen, profile, resetEdit]);

  const { data: stores } = useQuery({
    queryKey: ["user-stores", uid],
    queryFn: async () => getUserStores(user!, uid!),
    enabled: !!user && !!uid,
  });

  const { data: tokens } = useQuery({
    queryKey: ["user-tokens", uid],
    queryFn: async () => getUserDeviceTokens(user!, uid!),
    enabled: !!user && !!uid,
  });

  const { data: activity } = useQuery({
    queryKey: ["user-activity", uid, activityOffset],
    queryFn: async () =>
      getUserActivity(user!, uid!, { limit: ACTIVITY_PAGE_SIZE, offset: activityOffset }),
    enabled: !!user && !!uid,
  });

  const { data: guestlistCheckins } = useQuery({
    queryKey: ["user-guestlist-checkins", uid],
    queryFn: async () => getUserGuestlistCheckins(user!, uid!),
    enabled: !!user && !!uid,
  });

  const updateMutation = useMutation({
    mutationFn: (values: EditFormValues) =>
      updateUser(user!, uid!, {
        first_name: values.firstName,
        last_name: values.lastName,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["user", uid] });
      void qc.invalidateQueries({ queryKey: ["users"] });
      setEditOpen(false);
      toast.success("User updated.");
    },
    onError: (err) => setEditError("root", { message: (err as Error).message }),
  });

  const promoterMutation = useMutation({
    mutationFn: (isPromoter: boolean) => updateUser(user!, uid!, { is_promoter: isPromoter }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["user", uid] });
      void qc.invalidateQueries({ queryKey: ["users"] });
    },
    onError: (err) => toast.error((err as Error).message),
  });

  const notifMutation = useMutation({
    mutationFn: (values: NotificationFormValues) =>
      sendUserNotification(user!, uid!, {
        title: values.title,
        body: values.body,
        imageUrl: values.imageUrl || undefined,
      }),
    onSuccess: () => {
      setNotifOpen(false);
      resetNotif();
      toast.success("Notification sent.");
    },
    onError: (err) => setNotifError("root", { message: (err as Error).message }),
  });

  const suspendMutation = useMutation({
    mutationFn: async () => suspendUser(user!, uid!, suspendReason),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["user", uid] });
      void qc.invalidateQueries({ queryKey: ["users"] });
      setSuspendOpen(false);
      setSuspendReason("");
      toast.success("User suspended.");
    },
    onError: (err) => toast.error((err as Error).message),
  });

  const reinstateMutation = useMutation({
    mutationFn: async () => reinstateUser(user!, uid!),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["user", uid] });
      void qc.invalidateQueries({ queryKey: ["users"] });
      setReinstateOpen(false);
      toast.success("User reinstated.");
    },
    onError: (err) => toast.error((err as Error).message),
  });

  const flagNameMutation = useMutation({
    mutationFn: async () => flagUserName(user!, uid!, flagNameReason),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["user", uid] });
      void qc.invalidateQueries({ queryKey: ["users"] });
      setFlagNameOpen(false);
      setFlagNameReason("");
      toast.success("User name flagged.");
    },
    onError: (err) => toast.error((err as Error).message),
  });

  const unflagNameMutation = useMutation({
    mutationFn: async () => unflagUserName(user!, uid!),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["user", uid] });
      void qc.invalidateQueries({ queryKey: ["users"] });
      setUnflagNameOpen(false);
      toast.success("Name flag cleared.");
    },
    onError: (err) => toast.error((err as Error).message),
  });

  const deleteMutation = useMutation({
    mutationFn: async () => deleteUser(user!, uid!),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["users"] });
      void qc.invalidateQueries({ queryKey: ["deletion-requests"] });
      void qc.invalidateQueries({ queryKey: ["dashboard-summary"] });
      toast.success("User permanently deleted.");
      void navigate("/users");
    },
    onError: (err) => toast.error((err as Error).message),
  });

  if (isLoading) return <SpinnerCenter />;

  if (profileError)
    return (
      <div className="flex flex-col items-center justify-center gap-2 py-20 text-center">
        <AlertCircle className="size-8 text-muted-foreground/50" />
        <p className="font-medium text-muted-foreground">Failed to load user</p>
      </div>
    );

  if (!profile) return null;

  return (
    <div className="space-y-6 max-w-3xl">
      {/* Header */}
      <div className="flex items-center justify-between gap-2">
        <h1 className="text-2xl font-bold">
          {profile.first_name} {profile.last_name}
        </h1>
        <div className="flex gap-2 shrink-0">
          <Button variant="outline" size="sm" onClick={() => setEditOpen(true)}>
            <Pencil className="size-3.5 mr-1" /> Edit
          </Button>
          <Button variant="outline" size="sm" onClick={() => setNotifOpen(true)}>
            <Bell className="size-3.5 mr-1" /> Notify
          </Button>
        </div>
      </div>

      {/* Profile Info */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Profile Info</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-2 sm:grid-cols-2 text-sm">
          <div className="sm:col-span-2">
            <p className="text-xs text-muted-foreground">UID</p>
            <p className="font-mono break-all">{profile.user_uid}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Email</p>
            <p>{profile.email ?? "—"}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Phone</p>
            <p>{profile.phone_number ? formatPhone(profile.phone_number) : "—"}</p>
            {profile.phone_number && profile.phone_verified_at && (
              <p className="text-xs text-muted-foreground">
                Verified {formatDate(profile.phone_verified_at)}
              </p>
            )}
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Email Notifications</p>
            <p>{profile.email_notifications_enabled ? "Enabled" : "Disabled"}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground mb-1">Promoter</p>
            <Switch
              checked={profile.is_promoter}
              onCheckedChange={(checked) => promoterMutation.mutate(checked)}
              disabled={promoterMutation.isPending}
            />
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Joined</p>
            <p>{formatDate(profile.created_at)}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Last Updated</p>
            <p>{profile.updated_at ? formatDate(profile.updated_at) : "—"}</p>
          </div>
        </CardContent>
      </Card>

      {/* Suspension — platform-wide restriction on claiming/redeeming/registering/booking (see disca-backend/CLAUDE.md) */}
      <Card
        className={profile.is_suspended ? "border-orange-200 dark:border-orange-800" : undefined}
      >
        <CardHeader>
          <CardTitle
            className={cn(
              "flex items-center gap-2 text-base",
              profile.is_suspended && "text-orange-700 dark:text-orange-300"
            )}
          >
            <ShieldAlert className="size-4" /> {profile.is_suspended ? "Suspended" : "Suspension"}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {profile.is_suspended && (
            <div className="grid gap-2 sm:grid-cols-2 text-sm">
              {profile.suspended_at && (
                <div>
                  <p className="text-xs text-muted-foreground">Suspended At</p>
                  <p>{formatDateTime(profile.suspended_at)}</p>
                </div>
              )}
              {profile.suspended_reason && (
                <div className="sm:col-span-2">
                  <p className="text-xs text-muted-foreground">Reason</p>
                  <p>{profile.suspended_reason}</p>
                </div>
              )}
            </div>
          )}
          <div className="flex flex-wrap gap-2">
            {profile.is_suspended ? (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setReinstateOpen(true)}
                disabled={reinstateMutation.isPending}
              >
                Reinstate
              </Button>
            ) : (
              <Button variant="outline" size="sm" onClick={() => setSuspendOpen(true)}>
                Suspend
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Name Flag — blocks the app behind disca-app-flutter's name-correction overlay until a real name is submitted (see disca-backend/CLAUDE.md) */}
      <Card
        className={profile.is_name_flagged ? "border-orange-200 dark:border-orange-800" : undefined}
      >
        <CardHeader>
          <CardTitle
            className={cn(
              "flex items-center gap-2 text-base",
              profile.is_name_flagged && "text-orange-700 dark:text-orange-300"
            )}
          >
            <UserRoundX className="size-4" />{" "}
            {profile.is_name_flagged ? "Name Flagged" : "Name Flag"}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {profile.is_name_flagged && (
            <div className="grid gap-2 sm:grid-cols-2 text-sm">
              {profile.name_flagged_at && (
                <div>
                  <p className="text-xs text-muted-foreground">Flagged At</p>
                  <p>{formatDateTime(profile.name_flagged_at)}</p>
                </div>
              )}
              {profile.name_flagged_reason && (
                <div className="sm:col-span-2">
                  <p className="text-xs text-muted-foreground">Reason</p>
                  <p>{profile.name_flagged_reason}</p>
                </div>
              )}
            </div>
          )}
          <div className="flex flex-wrap gap-2">
            {profile.is_name_flagged ? (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setUnflagNameOpen(true)}
                disabled={unflagNameMutation.isPending}
              >
                Clear Flag
              </Button>
            ) : (
              <Button variant="outline" size="sm" onClick={() => setFlagNameOpen(true)}>
                Flag Name
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Store Booking Bans — read-only cross-store summary; each ban is imposed from that store's Bookings tab and lifted from its Appointments > Cancellation Policy sub-tab (see disca-backend/CLAUDE.md) */}
      {profile.store_bans && profile.store_bans.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <CalendarX className="size-4" /> Store Booking Bans
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="divide-y">
              {profile.store_bans.map((b) => (
                <li key={b.store_id} className="flex items-center justify-between gap-2 py-2">
                  <div className="min-w-0">
                    <Link
                      to={storeDetailPath(b.store_id, TAB_APPOINTMENTS)}
                      className="text-sm font-medium hover:underline"
                    >
                      {b.store_name}
                    </Link>
                    {b.reason && <p className="text-xs text-muted-foreground">{b.reason}</p>}
                  </div>
                  <span className="text-xs text-muted-foreground shrink-0">
                    Until {formatDateTime(b.banned_until)}
                  </span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      {/* Inviter */}
      {profile.inviter && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <UserPlus className="size-4" /> Inviter
            </CardTitle>
          </CardHeader>
          <CardContent className="flex items-center justify-between gap-2">
            <Link
              to={`/users/${profile.inviter.user_uid}`}
              className="text-sm font-medium hover:underline"
            >
              {profile.inviter.first_name} {profile.inviter.last_name}
            </Link>
            <span
              className={cn(
                "text-xs font-medium shrink-0",
                profile.inviter.rewarded ? "text-green-600" : "text-muted-foreground"
              )}
            >
              {profile.inviter.rewarded
                ? `Rewarded ${formatDate(profile.inviter.rewarded_at!)}`
                : "Pending"}
            </span>
          </CardContent>
        </Card>
      )}

      {/* Platform Invitees (outbound referrals) */}
      {profile.invitees && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Users className="size-4" />
              Platform Invitees
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground mb-3">
              {profile.invitees.total_invitees} friend
              {profile.invitees.total_invitees === 1 ? "" : "s"} joined ·{" "}
              {profile.invitees.rewarded_invitees} rewarded
            </p>
            <ul className="divide-y">
              {profile.invitees.list.map((inv) => (
                <li
                  key={inv.user_uid}
                  className="flex items-center justify-between gap-2 py-2 text-sm"
                >
                  <div className="min-w-0">
                    <Link to={`/users/${inv.user_uid}`} className="font-medium hover:underline">
                      {inv.first_name} {inv.last_name}
                    </Link>
                    <p className="text-xs text-muted-foreground">
                      Joined {formatDate(inv.joined_at)}
                    </p>
                  </div>
                  <span
                    className={cn(
                      "text-xs font-medium shrink-0",
                      inv.rewarded ? "text-green-600" : "text-muted-foreground"
                    )}
                  >
                    {inv.rewarded ? "Rewarded" : "Pending"}
                  </span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      {/* Points & Activity */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Points & Activity</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-2 sm:grid-cols-2 text-sm">
            <div>
              <p className="text-xs text-muted-foreground">Points Balance</p>
              <p>{formatInt(profile.points_balance)}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Accumulated Points</p>
              <p>{formatInt(profile.accumulated_points)}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Points Used</p>
              <p>{formatInt(profile.points_used)}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Rewards Claimed</p>
              <p>{formatInt(profile.point_rewards_redeemed)}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Lifetime Savings</p>
              <p>{formatCents(profile.saved_lifetime_cents)}</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 border-t pt-3">
            <Button variant="outline" size="sm" onClick={() => setAdjustPointsOpen(true)}>
              <Coins className="size-4" /> Adjust Points Balance
            </Button>
            <Button variant="outline" size="sm" onClick={() => setAdjustStampsOpen(true)}>
              <Coins className="size-4" /> Adjust Stamp Cards
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Stores */}
      {stores && stores.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Store className="size-4" /> Stores
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="divide-y">
              {stores.map((s) => (
                <li key={s.id} className="flex items-center justify-between py-2">
                  <Link to={`/stores/${s.id}`} className="text-sm font-medium hover:underline">
                    {s.name}
                  </Link>
                  <div className="flex items-center gap-2">
                    <StatusBadge status={s.status as "active" | "suspended" | "hidden"} />
                    <span className="text-xs text-muted-foreground capitalize">{s.role}</span>
                  </div>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      {/* Device tokens */}
      {tokens && tokens.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Device Tokens</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2">
              {tokens.map((t) => {
                const p = t.platform.toLowerCase();
                const label = p === "ios" ? "iOS" : p === "android" ? "Android" : t.platform;
                return (
                  <li key={t.id} className="flex items-center gap-3">
                    {p === "android" ? (
                      <AndroidIcon className="size-4 text-muted-foreground shrink-0" />
                    ) : p === "ios" ? (
                      <AppleIcon className="size-4 text-muted-foreground shrink-0" />
                    ) : (
                      <MonitorSmartphone className="size-4 text-muted-foreground shrink-0" />
                    )}
                    <span className="flex-1 text-xs text-muted-foreground">
                      Registered {formatDate(t.created_at)}
                    </span>
                    <span className="text-xs font-semibold text-primary bg-primary/10 rounded-full px-2.5 py-0.5">
                      {label}
                    </span>
                  </li>
                );
              })}
            </ul>
          </CardContent>
        </Card>
      )}

      {/* Activity */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Activity className="size-4" /> Activity
          </CardTitle>
        </CardHeader>
        <CardContent>
          {activity ? (
            <>
              {activity.data.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-6">No activity yet.</p>
              ) : (
                <ul className="divide-y">
                  {activity.data.map((a) => (
                    <li key={a.id} className="flex items-start justify-between py-2 text-sm gap-2">
                      <div className="min-w-0">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className="font-medium">{formatActionLabel(a.action)}</span>
                          {a.points_change !== 0 && (
                            <span
                              className={`text-xs ${a.points_change > 0 ? "text-green-600" : "text-red-600"}`}
                            >
                              {a.points_change > 0 ? "+" : ""}
                              {a.points_change} pts
                            </span>
                          )}
                          {a.stamps_change !== 0 && (
                            <span
                              className={`text-xs ${a.stamps_change > 0 ? "text-green-600" : "text-red-600"}`}
                            >
                              {a.stamps_change > 0 ? "+" : ""}
                              {a.stamps_change}{" "}
                              {Math.abs(a.stamps_change) === 1 ? "stamp" : "stamps"}
                            </span>
                          )}
                        </div>
                        {a.store_name && (
                          <p className="text-xs text-muted-foreground truncate">{a.store_name}</p>
                        )}
                        {invitedGuestName(a) && (
                          <p className="text-xs text-muted-foreground truncate">
                            Guest: {invitedGuestName(a)}
                          </p>
                        )}
                      </div>
                      <span className="text-xs text-muted-foreground shrink-0">
                        {formatDateTimeWithDay(a.created_at)}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
              {activity.data.length > 0 && (
                <div className="mt-4">
                  <Pagination
                    offset={activityOffset}
                    limit={ACTIVITY_PAGE_SIZE}
                    total={activity.total}
                    onOffsetChange={setActivityOffset}
                  />
                </div>
              )}
            </>
          ) : (
            <div className="flex justify-center py-6">
              <Spinner className="size-5 border-primary" />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Guestlist Checkins — events this user has invited >=1 checked-in guest to, via their own event invite link */}
      {guestlistCheckins && guestlistCheckins.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <CalendarCheck className="size-4" /> Guestlist Checkins
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="divide-y">
              {guestlistCheckins.map((c) => {
                const total = c.app_invitee_count + c.external_invitee_count;
                return (
                  <li key={c.event_id}>
                    <button
                      type="button"
                      onClick={() => setSelectedCheckinEvent(c)}
                      className="w-full text-left py-2 px-2 -mx-2 rounded-md hover:bg-muted/50"
                    >
                      <p className="text-sm font-medium">{c.event_title}</p>
                      <p className="text-xs text-muted-foreground">
                        {formatDate(c.event_starts_at)} · {c.venue_name || c.store_name}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {total} guest{total !== 1 ? "s" : ""} ({c.app_invitee_count} DISCA,{" "}
                        {c.external_invitee_count} Public)
                      </p>
                      {(c.points_earned > 0 || c.stamps_earned > 0) && (
                        <p className="text-xs mt-0.5">
                          {c.points_earned > 0 && (
                            <span className="text-green-600 font-medium">
                              +{c.points_earned} pts
                            </span>
                          )}
                          {c.points_earned > 0 && c.stamps_earned > 0 && "  "}
                          {c.stamps_earned > 0 && (
                            <span className="text-amber-600 font-medium">
                              +{c.stamps_earned} stamps
                            </span>
                          )}
                        </p>
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          </CardContent>
        </Card>
      )}

      {/* Danger zone — accordion matching disca-admin-flutter */}
      <div
        className={cn(
          "rounded-lg border-[1.5px] transition-colors",
          dangerExpanded ? "border-destructive/60" : "border-destructive/30"
        )}
      >
        <button
          className="flex w-full items-center gap-2 p-4 text-left"
          onClick={() => setDangerExpanded((v) => !v)}
        >
          <TriangleAlert className="size-5 shrink-0 text-destructive" />
          <span className="flex-1 text-sm font-semibold text-destructive">Danger Zone</span>
          {dangerExpanded ? (
            <ChevronUp className="size-4 text-destructive" />
          ) : (
            <ChevronDown className="size-4 text-destructive" />
          )}
        </button>
        {dangerExpanded && (
          <>
            <div className="h-px bg-destructive/30" />
            <div className="p-4 space-y-3">
              <p className="text-sm font-semibold text-destructive">Delete User</p>
              <p className="text-sm text-muted-foreground">
                Permanently delete this user from Firebase Auth and the database. All associated
                data will be removed. This action cannot be undone.
              </p>
              <Button
                variant="destructive"
                size="sm"
                onClick={() => setDeleteOpen(true)}
                disabled={deleteMutation.isPending}
              >
                <Trash2 className="size-4 mr-1" /> Delete User
              </Button>
            </div>
          </>
        )}
      </div>

      {/* Edit dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent onOpenAutoFocus={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle>Edit User</DialogTitle>
          </DialogHeader>
          <form
            onSubmit={handleEditSubmit((values) => updateMutation.mutate(values))}
            noValidate
            className="space-y-4"
          >
            <TextInput
              id="ue-first-name"
              label="First name"
              maxLength={50}
              error={editErrors.firstName?.message}
              {...registerEdit("firstName")}
            />
            <TextInput
              id="ue-last-name"
              label="Last name"
              maxLength={50}
              error={editErrors.lastName?.message}
              {...registerEdit("lastName")}
            />
            {editErrors.root?.message && (
              <p className="text-sm text-destructive">{editErrors.root.message}</p>
            )}
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setEditOpen(false)}
                disabled={updateMutation.isPending}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={updateMutation.isPending}>
                {updateMutation.isPending ? (
                  <Spinner className="size-4 border-primary-foreground" />
                ) : (
                  "Save"
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Notification dialog */}
      <Dialog
        open={notifOpen}
        onOpenChange={(open) => {
          setNotifOpen(open);
          if (!open) resetNotif();
        }}
      >
        <DialogContent onOpenAutoFocus={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle>Send Notification</DialogTitle>
          </DialogHeader>
          <form
            onSubmit={handleNotifSubmit((values) => notifMutation.mutate(values))}
            noValidate
            className="space-y-4"
          >
            <TextInput
              id="un-title"
              label="Title"
              maxLength={100}
              error={notifErrors.title?.message}
              {...registerNotif("title")}
            />
            <Textarea
              id="un-body"
              label="Body"
              rows={3}
              maxLength={500}
              error={notifErrors.body?.message}
              {...registerNotif("body")}
            />
            <TextInput
              id="un-image-url"
              label="Image URL (optional)"
              {...registerNotif("imageUrl")}
            />
            {notifErrors.root?.message && (
              <p className="text-sm text-destructive">{notifErrors.root.message}</p>
            )}
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setNotifOpen(false)}
                disabled={notifMutation.isPending}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={notifMutation.isPending}>
                {notifMutation.isPending ? (
                  <Spinner className="size-4 border-primary-foreground" />
                ) : (
                  "Send"
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation — requires typing full name */}
      <ConfirmDialog
        open={deleteOpen}
        onOpenChange={(open) => {
          if (!open) {
            setDeleteOpen(false);
            setDeleteConfirmName("");
          }
        }}
        title="Delete User"
        description={`This will permanently delete "${profile.first_name} ${profile.last_name}" from Firebase Auth and the database, including all their data.`}
        confirmLabel="Delete Permanently"
        destructive
        isPending={deleteMutation.isPending}
        confirmInput={{
          value: deleteConfirmName,
          onChange: setDeleteConfirmName,
          placeholder: `${profile.first_name} ${profile.last_name}`,
          requiredValue: `${profile.first_name} ${profile.last_name}`,
        }}
        onConfirm={() => deleteMutation.mutate()}
      />

      {/* Suspend confirmation */}
      <ConfirmDialog
        open={suspendOpen}
        onOpenChange={(open) => {
          setSuspendOpen(open);
          if (!open) setSuspendReason("");
        }}
        title="Suspend user"
        description={`Suspend "${profile.first_name} ${profile.last_name}" from claiming, redeeming, registering, and booking on the platform?`}
        confirmLabel="Suspend"
        destructive
        isPending={suspendMutation.isPending}
        onConfirm={() => suspendMutation.mutate()}
        textarea={{
          value: suspendReason,
          onChange: setSuspendReason,
          label: "Reason (optional)",
          placeholder: "e.g. repeated harassment of staff",
          maxLength: 500,
        }}
      />

      {/* Reinstate confirmation */}
      <ConfirmDialog
        open={reinstateOpen}
        onOpenChange={setReinstateOpen}
        title="Reinstate user"
        description={`Lift the suspension for "${profile.first_name} ${profile.last_name}"? They'll be able to use the platform again.`}
        confirmLabel="Reinstate"
        isPending={reinstateMutation.isPending}
        onConfirm={() => reinstateMutation.mutate()}
      />

      {/* Flag name confirmation */}
      <ConfirmDialog
        open={flagNameOpen}
        onOpenChange={(open) => {
          setFlagNameOpen(open);
          if (!open) setFlagNameReason("");
        }}
        title="Flag name for correction"
        description={`Flag "${profile.first_name} ${profile.last_name}"'s name as needing correction? They'll be blocked from using the app until they submit a real name.`}
        confirmLabel="Flag Name"
        destructive
        isPending={flagNameMutation.isPending}
        onConfirm={() => flagNameMutation.mutate()}
        textarea={{
          value: flagNameReason,
          onChange: setFlagNameReason,
          label: "Reason (optional)",
          placeholder: "e.g. looks like a fake name",
          maxLength: 500,
        }}
      />

      {/* Clear name flag confirmation */}
      <ConfirmDialog
        open={unflagNameOpen}
        onOpenChange={setUnflagNameOpen}
        title="Clear name flag"
        description={`Clear the name flag for "${profile.first_name} ${profile.last_name}"? They'll be able to use the app again without changing their name.`}
        confirmLabel="Clear Flag"
        isPending={unflagNameMutation.isPending}
        onConfirm={() => unflagNameMutation.mutate()}
      />

      <AdjustPointsBalanceDialog
        open={adjustPointsOpen}
        onOpenChange={setAdjustPointsOpen}
        uid={uid!}
        currentBalance={profile.points_balance}
      />

      <AdjustStampCardDialog
        open={adjustStampsOpen}
        onOpenChange={setAdjustStampsOpen}
        uid={uid!}
      />

      {/* Checked-in guest names for a single event, opened from the Guestlist Checkins card above */}
      <Dialog
        open={!!selectedCheckinEvent}
        onOpenChange={(open) => !open && setSelectedCheckinEvent(null)}
      >
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Checked In ({selectedCheckinEvent?.invitees.length ?? 0})</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground -mt-2">{selectedCheckinEvent?.event_title}</p>
          <ul className="divide-y">
            {selectedCheckinEvent?.invitees.map((inv, i) => (
              <li key={i} className="flex items-center justify-between gap-2 py-2 text-sm">
                <span>
                  {inv.first_name} {inv.last_name}
                </span>
                <span
                  className={cn(
                    "text-xs font-medium shrink-0",
                    inv.is_app_user ? "text-green-600" : "text-muted-foreground"
                  )}
                >
                  {inv.is_app_user ? "Checked in" : "Public"}
                </span>
              </li>
            ))}
          </ul>
        </DialogContent>
      </Dialog>
    </div>
  );
}
